import { isDefined, isNotDefined } from '@sgme/fp';
import type { ChangeEvent } from 'react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { useAlgoTileId, useValidateAlgo } from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectAlgoErrors,
  selectOrderEntryByIdFromBlotter,
  selectTradeIdFromAlgoTile,
} from '@/store/orders/algoOrders/selectors.ts';
import { type Speed, speeds } from '@/store/orders/algoOrders/state.ts';

type AlgoSpeedProps = {
  speed: Speed | undefined;
  setSpeed: (value: Speed | undefined) => void;
};

export const AlgoEditingSpeed = (props: AlgoSpeedProps) => {
  const { speed, setSpeed } = props;
  const tileId = useAlgoTileId();
  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromAlgoTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );
  const value = speed ?? (isDefined(orderFromBlotter?.speed) ? orderFromBlotter?.speed : undefined);
  const errors = useAppSelector((state) => selectAlgoErrors(state, tileId));

  const SpeedError = errors?.speed;

  const validateAlgo = useValidateAlgo();

  const handleSpeedChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    track('Order action', 'Speed changed');
    const newSpeed = event.target.value as Speed;
    if (isDefined(orderFromBlotter)) {
      const [currency1, currency2] = isDefined(orderFromBlotter?.currencyPair)
        ? orderFromBlotter.currencyPair.split('/')
        : ['', ''];

      if (isNotDefined(currency1) || isNotDefined(currency2)) {
        console.error(`[handleSpeedChange] Invalid currency pair: ${currency1}/${currency2}`);
        return;
      }

      const response = await validateAlgo({
        speed: newSpeed,
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setSpeed(newSpeed);
      }
    }
  };

  return (
    <OrderRow labelId="order.fields.speed">
      <OmsFieldErrorWrapper errors={SpeedError}>
        <Form.Select value={value} onChange={handleSpeedChange} size="sm" data-e2e="speed-selector">
          {speeds.map((speed) => (
            <option key={speed} value={speed}>
              <FormattedMessage id={`order.fields.speed.${speed}`} />
            </option>
          ))}
        </Form.Select>
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
